import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { CENTRAL_PHARMACY_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY, statusLabel } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, formatDate, formatNumber } from '../ui';
import { ORDER_STATUSES, nextActionLabel, orderStatusClass } from '../components/orderUi';

const copy = PHARMACY_COPY.ORDERS;

export default function PharmacyOrdersPage() {
  const navigate = useNavigate();
  const { centralSite } = usePharmacy();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.listOrders({ status: statusFilter || undefined });
      setRows(res.success ? res.data || [] : []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const list = !search
      ? rows
      : rows.filter((o) => {
          const hay = `${o.order_number || ''} ${o.supplier_name || ''}`.toLowerCase();
          return hay.includes(search);
        });
    return [...list].sort((a, b) => Number(b.id) - Number(a.id));
  }, [rows, search]);

  const stats = useMemo(() => {
    const open = rows.filter((o) => !['RECEIVED', 'CANCELLED'].includes(o.status)).length;
    const received = rows.filter((o) => o.status === 'RECEIVED').length;
    return { total: rows.length, open, received };
  }, [rows]);

  const openCreate = () => navigate(CENTRAL_PHARMACY_PATHS.ORDER_NEW);
  const openDetail = (order) => navigate(CENTRAL_PHARMACY_PATHS.ORDER_DETAIL(order.id));

  return (
    <div className="cp-orders-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={
          centralSite
            ? `${copy.DESC} Réception sur « ${centralSite.name} ».`
            : copy.DESC
        }
        actions={
          <button type="button" className="cp-btn cp-btn--primary" onClick={openCreate}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      <section className="cp-catalog-stats" aria-label="Synthèse commandes">
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_TOTAL}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.total)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--amber">
          <span className="cp-catalog-stat__label">{copy.STAT_OPEN}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.open)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--ok">
          <span className="cp-catalog-stat__label">{copy.STAT_RECEIVED}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.received)}</span>
        </article>
      </section>

      <div className="cp-card">
        <div className="cp-catalog-toolbar">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>
          <div className="cp-catalog-toolbar__end">
            <div className="cp-filter-pills" role="group" aria-label="Statut commande">
              {ORDER_STATUSES.map((s) => (
                <button
                  key={s || 'all'}
                  type="button"
                  className={`cp-filter-pill${statusFilter === s ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === '' ? copy.FILTER_ALL : statusLabel(s)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={17} aria-hidden />
            </button>
            <div className="cp-catalog-meta">
              <strong>{loading ? '—' : formatNumber(filteredRows.length)}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock label="Chargement des commandes…" />
          ) : filteredRows.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-state__icon" aria-hidden>
                <Icon name="inbox" size={28} />
              </span>
              <h3 className="cp-empty-state__title">{copy.EMPTY_TITLE}</h3>
              <p className="cp-empty-state__text">{copy.EMPTY_HINT}</p>
              <button type="button" className="cp-btn cp-btn--primary" onClick={openCreate}>
                <Icon name="plus" size={16} aria-hidden />
                {copy.EMPTY_CTA}
              </button>
            </div>
          ) : (
            <div className="cp-table-wrap">
              <Table responsive className="cp-table cp-orders-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_NUMBER}</th>
                    <th>{copy.COL_SUPPLIER}</th>
                    <th className="text-end">{copy.COL_LINES}</th>
                    <th>{copy.COL_EXPECTED}</th>
                    <th>{copy.COL_STATUS}</th>
                    <th className="text-end">{copy.COL_ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((o) => (
                    <tr
                      key={o.id}
                      className="cp-orders-table__row"
                      onClick={() => openDetail(o)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetail(o);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                    >
                      <td>
                        <div className="cp-med-cell">
                          <span className="cp-med-cell__icon" aria-hidden>
                            <Icon name="inbox" size={16} />
                          </span>
                          <span className="cp-med-cell__body">
                            <span className="cp-med-cell__name">{o.order_number}</span>
                            <span className="cp-med-cell__desc">{nextActionLabel(o.status)}</span>
                          </span>
                        </div>
                      </td>
                      <td>{o.supplier_name || '—'}</td>
                      <td className="text-end cp-num">{formatNumber(o.items_count)}</td>
                      <td>{o.expected_date ? formatDate(o.expected_date) : '—'}</td>
                      <td>
                        <span className={`cp-status-pill ${orderStatusClass(o.status)}`}>
                          {statusLabel(o.status)}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="cp-btn cp-btn--ghost cp-btn--sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(o);
                          }}
                        >
                          {copy.OPEN}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
